import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Space,
  Button,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
  Divider,
  List,
  Badge,
  Tooltip,
  Avatar
} from "antd";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  BookOutlined,
  CheckSquareOutlined,
  PlusOutlined,
  UserOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  LinkedinOutlined,
  IdcardOutlined,
  FolderOutlined,
  TagOutlined,
  TeamOutlined
} from "@ant-design/icons";
import useCourseDetails from "../Hooks/Courses/useCourseDetails";
import { ExpertRecommendationLevelEnumLabels } from "Common/Constants/Enums/ExpertRecommendation";
const { Title, Text, Paragraph } = Typography;

const CourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { course, loading, error } = useCourseDetails(courseId);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes, seconds] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const getStatusTag = (isPublished) => {
    return (
      <Tag
        color={isPublished ? "green" : "orange"}
        icon={isPublished ? <CheckCircleOutlined /> : <SettingOutlined />}
        className="tagStyle"
      >
        {isPublished ? "Published" : "Draft"}
      </Tag>
    );
  };

  // Get author information from course object
  const getAuthorInfo = () => {
    if (course.courseAuthor) {
      return {
        name: course.courseAuthor.name || "Unknown Author",
        email: course.courseAuthor.email || "No email provided",
        linkedin: course.courseAuthor.linkedinUrl || course.linkedinUrl || null
      };
    }
    return {
      name: course.authorName || "Unknown Author",
      email: course.authorEmail || "No email provided",
      linkedin: course.authorLinkedIn || course.linkedinUrl || null
    };
  };

// Logic to convert days to readable Y/M/D
  const formatDuration = (totalDays) => {
    if (!totalDays) return "Not specified";
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = (totalDays % 365) % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(", ");
  };

  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "Not specified";

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    }

    return `${remainingMinutes} Minutes`;
  };

  // Handle module click - navigate to module details
  const handleModuleClick = (moduleId) => {
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
  };

  if (loading) {
    return (
      <div className="loadingSpinner">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Course Details"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!course) {
    return (
      <Alert
        message="Course Not Found"
        description="The requested course could not be found."
        type="warning"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  const authorInfo = getAuthorInfo();

  return (
    <Card
      className="card-professional"
      title={
        <Space direction="vertical" size={2}>
          <Title level={2} style={{ margin: 0, color: "white" }}>
            {course.title}
          </Title>
          <Space size="middle">
            {getStatusTag(course.isPublished)}
            <Text style={{ color: "rgba(255,255,255,0.8)" }}>
              Course ID: {course.id}
            </Text>
          </Space>
        </Space>
      }
      extra={
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/proAdmin-dashboard/courses`)}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "white",
          }}
        >
          Back to Courses 
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Course Information */}
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={16}>
            {/* Course Description Card */}
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: "12px", 
              border: '2px solid #f0f0f0' 
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space>
                  <FileTextOutlined style={{ color: '#9A6B0E' }} />
                  <Text strong style={{ fontSize: '16px' }}>Course Description</Text>
                </Space>
                <Paragraph style={{ lineHeight: '1.6', margin: 0, color: '#000' }}>
                  {course.description}
                </Paragraph>
              </Space>
            </div>

            {/* Course Modules Card */}
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              border: '1px solid #f0f0f0' 
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <BookOutlined style={{ color: '#9A6B0E' }} />
                    <Text strong style={{ fontSize: '16px' }}>Course Modules</Text>
                    <Badge
                      count={course.modules?.length || 0}
                      showZero
                      color={course.modules?.length > 0 ? 'blue' : 'default'}
                    />
                  </Space>

                  {/* Only show Add Module button for draft courses */}
                  {!course.isPublished && (
                    <Tooltip title="Add Module">
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate(`/proAdmin-dashboard/courses/${course.id}/add-module`)}
                      >
                        Add Module
                      </Button>
                    </Tooltip>
                  )}
                </div>

                {course.modules && course.modules.length > 0 ? (
                  <List
                    size="small"
                    dataSource={course.modules}
                    renderItem={(module, index) => (
                      <List.Item
                        key={module.id}
                        onClick={() => handleModuleClick(module.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <List.Item.Meta
                          avatar={
                            <h4>{index + 1}</h4>
                          }
                          title={
                            <Space>
                              <Text strong className="title" style={{ color: '#000' }}>{module.title}</Text>
                              {module.isFinalized && (
                                <Tag color="green" icon={<CheckCircleOutlined />} className="tagStyle">
                                  Finalized
                                </Tag>
                              )}
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Paragraph
                                ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
                                style={{ margin: 0, color: '#666' }}
                                className="course-details"
                              >
                                {module.description}
                              </Paragraph>
                              <Space size="middle">
                                <Text style={{ color: '#666' }} className="course-details">
                                  <ClockCircleOutlined /> {formatTime(module.estimatedTime)}
                                </Text>
                                <Text style={{ color: '#666' }} className="course-details">
                                  Order: {module.order}
                                </Text>
                                <Text style={{ color: '#666' }} className="course-details">
                                  Pages: {module.pages?.length || 0}
                                </Text>
                                {module.hasAssessment ? (
                                  <Tag color="green" icon={<CheckCircleOutlined />}>
                                    Assessment Added
                                  </Tag>
                                ) : (
                                  <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                                    No Assessment
                                  </Tag>
                                )}
                              </Space>

                              {/* Alert for modules without assessment - only show for draft courses */}
                              {!module.hasAssessment && !course.isPublished && (
                                <Alert
                                  message="Assessment Required"
                                  description="This module doesn't have an assessment yet. Add an assessment to evaluate student learning."
                                  type="warning"
                                  showIcon
                                  action={
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${module.id}/edit-assessment`);
                                      }}
                                    >
                                      Add Assessment
                                    </Button>
                                  }
                                  style={{
                                    marginTop: '8px',
                                    borderRadius: '6px'
                                  }}
                                />
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <BookOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Title level={4} style={{ color: '#666' }}>
                      No Modules Added
                    </Title>
                    <Text style={{ color: '#666' }}>
                      Start by adding your first module to this course
                    </Text>
                    <div style={{ marginTop: '16px' }}>
                      <Tooltip
                        title={course.isPublished ? "Cannot add module to published course" : "Add Module"}
                      >
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          disabled={course.isPublished}
                          onClick={() => navigate(`/proAdmin-dashboard/courses/${course.id}/add-module`)}
                        >
                          Add First Module
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </Space>
            </div>
          </Col>

          <Col xs={24} lg={8}>
            {/* Course Overview Card */}
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              border: '1px solid #f0f0f0' 
            }}>
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Space>
                  <UserOutlined style={{ color: '#9A6B0E' }} />
                  <Text strong style={{ fontSize: '16px' }}>Course Overview</Text>
                </Space>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Status:</Text>
                  {getStatusTag(course.isPublished)}
                </div>

               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <Text>Duration:</Text>
  <Space>
    <ClockCircleOutlined />
    <Text>{formatDuration(course.durationInDays)}</Text>
  </Space>
</div>


                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Total Modules:</Text>
                  <Text>{course.modules?.length || 0}</Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Prerequisites:</Text>
                  <Text>{course.coursePrerequisites?.length || 0}</Text>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Created:</Text>
                  <Space>
                    <CalendarOutlined />
                    <Text>{formatDate(course.createdOn)}</Text>
                  </Space>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Last Updated:</Text>
                  <Space>
                    <CalendarOutlined />
                    <Text>{formatDate(course.lastModifiedOn)}</Text>
                  </Space>
                </div>
              </Space>
            </div>
            {/* Pricing Card */}
<div
  style={{
    background: "white",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #f0f0f0",
    marginTop: "12px"
  }}
>
  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
    <Space>
      <TagOutlined style={{ color: "#9A6B0E" }} />
      <Text strong style={{ fontSize: "16px" }}>Pricing</Text>
    </Space>

    {/* FREE COURSE */}
    {!course.isPaid && (
      <Tag
        color="green"
        style={{
          alignSelf: "flex-start",
          fontSize: "14px",
          padding: "4px 10px",
          borderRadius: "6px"
        }}
      >
        FREE COURSE
      </Tag>
    )}

    {/* PAID COURSE */}
    {course.isPaid && course.pricing && (
      <>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text type="secondary">Price</Text>
          <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
            {course.pricing.currency} {course.pricing.price}
          </Text>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text type="secondary">Access Duration</Text>
          <Text>
            {formatDuration(course.pricing.accessDurationDays)}
          </Text>
        </div>

        {/* EXPERT SUPPORT SECTION */}
        <Divider style={{ margin: "12px 0" }} />
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <TeamOutlined />
            <Text strong>Expert Support</Text>
            {course.hasActiveExpertSupport ? (
              <Tag color="gold">Active</Tag>
            ) : (
              <Tag color="orange">Not Added</Tag>
            )}
          </Space>
        </div>

        {/* EXPERT SUPPORT DETAILS */}
        {course.courseExpertSupport ? (
          <div style={{ 
            background: "#f8f9fa", 
            padding: "12px", 
            borderRadius: "6px",
            marginTop: "8px"
          }}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong style={{ fontSize: "14px" }}>Expert Details</Text>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <Avatar
                src={course.courseExpertSupport.expert?.profileImage}
                icon={<UserOutlined />}
                size="small"
              />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Text strong>
                    {course.courseExpertSupport.expert?.firstName} {course.courseExpertSupport.expert?.lastName}
                  </Text>
                  <Tag color="gold" style={{ fontSize: "11px" }}>
                    {ExpertRecommendationLevelEnumLabels[course.courseExpertSupport.recommendationLevel]}
                  </Tag>
                </div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {course.courseExpertSupport.expert?.yearsOfExperience} years experience
                </Text>
              </div>
            </div>

            <div style={{ marginBottom: "8px" }}>
              <Text strong style={{ fontSize: "13px", display: "block", marginBottom: "4px" }}>Service:</Text>
              <Text style={{ fontSize: "13px" }}>{course.courseExpertSupport.title}</Text>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>Unit Price:</Text>
              <Text strong style={{ fontSize: "13px" }}>
                ${course.courseExpertSupport.unitPrice}
              </Text>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>Unit Duration:</Text>
              <Text style={{ fontSize: "13px" }}>
                {formatMinutesToHours(course.courseExpertSupport.unitDurationMinutes)}
              </Text>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>Units Available:</Text>
              <Text style={{ fontSize: "13px" }}>
                {course.courseExpertSupport.allowedUnits}
              </Text>
            </div>
          </div>
        ) : (
          <div style={{
            padding: "12px",
            background: "#f8f9fa",
            borderRadius: "6px",
            textAlign: "center"
          }}>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              Expert support not available for this course
            </Text>
          </div>
        )}
      </>
    )}
  </Space>
</div>


            {/* Category & Subcategories Card - Moved to right side below Course Overview */}
         {course.category && (
  <div style={{ 
    background: 'white', 
    padding: '16px', 
    borderRadius: '6px', 
    marginTop: '12px', 
    border: '1px solid #f0f0f0'
  }}>
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Space>
        <FolderOutlined />
        <Text strong style={{ fontSize: '15px' }}>Category & Subcategories</Text>
      </Space>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '12px',
        flexWrap: 'wrap',
        marginTop: '8px'
      }}>
        {/* Main Category */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px',
          padding: '6px 0',
          width: '100%'
        }}>
          <FolderOutlined style={{ fontSize: '14px', color: '#666' }} />
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Category</Text>
            <Text strong style={{ color: '#000', fontSize: '14px' }}>{course.category.name}</Text>
          </div>
        </div>

        {/* Subcategories */}
        {course.category.subCategories && course.category.subCategories.length > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '6px',
            padding: '6px 0',
            width: '100%'
          }}>
            <TagOutlined style={{ fontSize: '14px', marginTop: '2px', color: '#666' }} />
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Subcategories ({course.category.subCategories.length})
              </Text>
              <Space wrap style={{ width: '100%' }}>
                {course.category.subCategories.map((sub, index) => (
                  <Tag 
                    key={sub.id} 
                    style={{ 
                      fontSize: '12px', 
                      padding: '2px 6px',
                      margin: '0 4px 4px 0',
                      maxWidth: '100%',
                      background: 'transparent',
                      borderColor: '#d9d9d9',
                      color: '#000'
                    }}
                  >
                    {sub.name}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        )}
      </div>
    </Space>
  </div>
)}

            {/* Prerequisites Card */}
            {course.coursePrerequisites && course.coursePrerequisites.length > 0 && (
              <div style={{ 
                background: 'white', 
                padding: '20px', 
                borderRadius: '8px', 
                border: '1px solid #f0f0f0', 
                marginTop: '12px' 
              }}>
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <Space>
                    <CheckSquareOutlined style={{ color: '#9A6B0E' }} />
                    <Text strong style={{ fontSize: '16px' }}>Prerequisites</Text>
                  </Space>
                  <List
                    size="small"
                    dataSource={course.coursePrerequisites}
                    renderItem={(prerequisite) => (
                      <List.Item key={prerequisite.id} style={{ padding: '4px 0' }}>
                        <Space>
                          <CheckSquareOutlined style={{ color: '#52c41a' }} />
                          <Text>{prerequisite.description}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                </Space>
              </div>
            )}

            {/* Author Information Card */}
            <div style={{ 
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
              marginTop: '12px',
              color: "black"
            }}>
              <div style={{ marginBottom: '12px' }}>
                <Space>
                  <IdcardOutlined style={{ color: '#9A6B0E' }} />
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Author Information</span>
                </Space>
              </div>

              <Space size={24} wrap>
                <Space>
                  <UserOutlined style={{ color: '#666' }} />
                  <Text strong>Name:</Text>
                  <Text>{authorInfo.name}</Text>
                </Space>
                
                <Space>
                  <MailOutlined style={{ color: '#666' }} />
                  <Text strong>Email:</Text>
                  <Text>{authorInfo.email}</Text>
                </Space>
                
                <Space>
                  <LinkedinOutlined style={{ color: '#666' }} />
                  <Text strong>LinkedIn:</Text>
                  {authorInfo.linkedin ? (
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(authorInfo.linkedin, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      View
                    </Button>
                  ) : (
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                      Not provided
                    </Text>
                  )}
                </Space>
              </Space>
            </div>
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default CourseDetails;